import * as R from 'ramda';
import React from 'react';
import {mapProps} from 'recompose';
import {startWith} from 'rxjs/operators';

import {translate} from 'iss/strings';
import {partyActivationTypes, profitCentersByContractExternalId, serviceTypes, serviceTypesByCategory, siteSizes, siteTypes} from 'iss/service/ActivationDataService';
import {eventCancelReasons} from 'iss/service/EvaluationEventService';
import {getScales} from 'iss/service/GradingScaleService';
import {questionGroups} from 'iss/service/QuestionService';
import subscribeTo, {SubscribeTo} from 'iss/utils/subscriptionUtils';
import {
  translateActivationReportingStyle,
  translateAnswerVisibility,
  translateEvaluationType,
  translateEventCancelReason,
  translateFormEvaluationContext,
  translatePartyActivationType,
  translateRecurrenceType,
  translateSiteSize
} from 'iss/widgets/domainTranslations';
import {Field, FormFieldLoadingError, RadioGroup, Select} from 'iss/widgets/Forms';

export {PartySelector, ActivationPartySelector} from 'iss/widgets/selectors/PartySelectors';
export {EvaluationFormSelector} from 'iss/widgets/selectors/EvaluationFormSelector';
export {EvaluationContractSelector} from 'iss/widgets/selectors/EvaluationContractSelector';
export * from 'iss/widgets/selectors/SplittingContractSelector';

export const QuestionGroupSelector = subscribeTo(questionGroups, 'questionGroups', function groupSelector({questionGroups, ...props}) {
  const options = R.map(({description, id}) => ({label: description, value: id}), questionGroups);
  return <Select options={options} {...props} />;
}, FormFieldLoadingError);

export const GradingScaleSelector = subscribeOptionsTo(Select, getScales);

export const GradeSelector = ({scale, ...props}) => {
  const options = R.map(({text, id}) => ({label: text, value: id}), scale.grades);
  return <Select options={options} {...props} />;
};

export const SiteTypeSelector = subscribeOptionsTo(Select, () => siteTypes);
export const ServiceTypeSelector = subscribeOptionsTo(Select, () => serviceTypes);
export const ProfitCenterSelector = subscribeOptionsTo(
  withEmptyOption('evaluationActivator.chooseProfitCenter')(Select),
  ({contractExtId}) => profitCentersByContractExternalId(contractExtId).pipe(startWith([]))
);

export const SiteSizeSelector = provideOptionsTo(Select,
  toOptions(translateSiteSize, [null, ...siteSizes]));
export const PartyActivationTypeSelector = provideOptionsTo(Select,
  toOptions(translatePartyActivationType, Object.values(partyActivationTypes)),
  {clearable: false}
);
export const EvaluationTypeSelector = provideOptionsTo(RadioGroup,
  toOptions(translateEvaluationType, ["REGULAR_EVALUATION", "TWO_PHASED_EVALUATION", "FOUR_PHASED_EVALUATION"]));
export const EventCancelReasonSelector = provideOptionsTo(
  withEmptyOption('domain.evaluationEvent.eventCancelReasons.chooseCancellingReason')(Select),
  toOptions(translateEventCancelReason, eventCancelReasons),
  {clearable: false}
);
export const ActivationReportingStyleSelector = provideOptionsTo(RadioGroup,
  toOptions(translateActivationReportingStyle, ["GRADE_AVERAGE", "SUCCESS_PERCENTAGE", "QUALITY_PERCENTAGE"]));

export const FormContextSelector = provideOptionsTo(RadioGroup,
  toOptions(translateFormEvaluationContext, ["WORK_SECURITY", "OTHER", "NOT_IN_USE"]));

export const AnswerVisibilitySelector = provideOptionsTo(RadioGroup,
  toOptions(translateAnswerVisibility, ["EVALUATOR_ONLY", "EVALUATOR_AND_SUBJECT"]));

export const ServiceTypeRadioSelector = subscribeTo(serviceTypesByCategory, 'serviceTypesByCategory', function radioGroupWithName({serviceTypesByCategory, ...props}) {
  const options = toOptionsWithIds(R.prop('name'), serviceTypesByCategory);
  return <RadioGroup options={options} {...props} />;
}, FormFieldLoadingError);
export const ServiceRadioSelector = subscribeTo(serviceTypesByCategory, 'serviceTypes', function ServiceSelector({serviceTypes, serviceType, ...props}) {
  const category = R.find(cat => cat.id == serviceType, serviceTypes);
  if (!category) return null;
  const options = toOptionsWithIds(R.prop('name'), category.services);
  return <RadioGroup options={options} {...props} />;
}, FormFieldLoadingError);

export function RecurrenceTypeSelector(props) {
  const avoidsWeekends = ['MONTHLY', 'CUSTOM_DATES'];
  const showAvoidWeekendsHelpText = R.flip(R.includes)(avoidsWeekends);
  const options = toOptions(translateRecurrenceType, ['WEEKLY', 'MONTHLY', 'CUSTOM_DATES', 'MANUAL']);
  return <React.Fragment>
    <Select clearable={false} options={options} {...props} />
    <Field valuePath={props.valuePath} pure={true}>
      {(fieldApi) => showAvoidWeekendsHelpText(fieldApi.getValue()) ?
        <small>{translate('evaluationActivator.weekendAvoidanceExplanation')}</small>
        : null }
    </Field>
  </React.Fragment>;
}

export function AnswerRadioSelector({grades, ...props}) {
  const options = toOptionsWithIds(R.prop('text'), grades);
  return <RadioGroup options={options} {...props} />;
}

export function AnswerHeadingSelector({headings, ...props}) {
  const options = toOptions(R.prop('title'), headings);
  return <Select clearable={false} options={options} {...props} />;
}

function toOptionsWithIds(getLabel, objects) {
  return R.map(object => ({
    value: object.id,
    label: getLabel(object)
  }), objects);
}

export function toOptions(getLabel, objects) {
  return objects.map(object => ({
    value: object,
    label: getLabel(object)
  }));
}

function withEmptyOption(label) {
  return mapProps(R.evolve({
    options: R.prepend(emptyOption(label))
  }));
}

function emptyOption(label) {
  return {
    value: null,
    label: translate(label),
  };
}

function subscribeOptionsTo(Component, getObservable, getLabel = R.prop("name"), additionalProps = {}) {
  return props => <SubscribeTo observable={getObservable(props)}
    errorHandler={(subscriptionProps) => <FormFieldLoadingError {...subscriptionProps} {...props} />}>
    {options => <Select options={toOptions(getLabel, options)} {...additionalProps} {...props} />}
  </SubscribeTo>;
}

function provideOptionsTo(Component, options, additionalProps = {}) {
  return (props) => <Component options={options} {...additionalProps} {...props} />;
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/Selectors.jsx